import React from "react";
import * as T from "./types";
import * as S from "./styles";
import EditColorIcon from "common/icons/editColorIcon";
import EditStarIcon from "common/icons/editStarIcon";
import EditTextIcon from "common/icons/editTextIcon";
import EditShadowIcon from "common/icons/editShadowIcon";
import EditDotIcon from "common/icons/editDotIcon";
import LinkIcon from "common/icons/linkIcon";
import EditButtonIcon from "common/icons/editButtonIcon";
import EditButtonTextIcon from "common/icons/editButtonTextIcon";
import ColorGenerator from "components/ColorGenerator";
import Slider from "components/Slider";
import { Context } from "../../context";
import { cloneDeep } from "lodash";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "store/types";
import { getFixedOpacity, saveWidgetsConfig } from "../../state/utils";
import ComboBox from "components/ComboBox";
import { TCButton } from "components/TCButton";
import { TC_BUTTON_TYPE } from "components/TCButton/constants";

const DEFAULT_OPTION = {
  id: "background",
  type: "color",
  label: "Background Fill",
};

const StylesPanel: React.FC<T.IProps> = (props) => {

  const context = React.useContext(Context);

  const componentRef = React.useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [option, setOption] = React.useState<T.ISelectedOption>(DEFAULT_OPTION);
  const [textPickerId, setTextPickerId] = React.useState<string>();
  const [hasChanged, setHasChanged] = React.useState(false);
  const [willClose, setWillClose] = React.useState(false);

  const dispatch = useDispatch();
    const {
        widgets,
        reviewsConfig,
    } = useSelector((rootState: RootState) => {
        return {
            widgets: rootState.reviews.widgets,
            reviewsConfig: rootState.reviews.reviewsConfig,
        }
    }, shallowEqual);

  const handleClickOutside = (e: MouseEvent) => {
    if (componentRef?.current && e.target) {
        if (!componentRef.current.contains(e.target as Node)) setWillClose(true);
    }
  }

  const onChangeEditingStatus = (color?: string, opacity?: number) => {
    if (option) { 
      setHasChanged(true);
      const newEditingStatus = cloneDeep(context.editingStatus);
      if (color) newEditingStatus[option.id].color = color;
      if (opacity !== undefined) newEditingStatus[option.id].opacity = opacity;
      context.handleEditingStatus(newEditingStatus);
    }
  }

  const onChangeColor = (color: string) => onChangeEditingStatus(color);
  const onChangeOpacity = (id: string, opacity: number) => onChangeEditingStatus(undefined, opacity);
  const onFinishChangeOpacity = (id: string, opacity: number) => {}

  const onFinishChange = async (attribute?: string, value?: string) => {
    setIsSaving(true);
    if (widgets) {
      try {
        let newWidgets = cloneDeep(widgets);
        if (option) {
          if (option.id === "star" && props.selectedTab === "entire") {
            Object.keys(newWidgets.entireConfig).forEach((key) => {
              newWidgets.entireConfig[key][`${option.id}Color`] = context.editingStatus[option.id].color;
              newWidgets.entireConfig[key][`${option.id}Opacity`] = context.editingStatus[option.id].opacity;
          });
          } else {
            newWidgets[`${props.selectedTab}Config`][props.selectedWidget.id][`${option.id}Color`] = context.editingStatus[option.id].color;
            newWidgets[`${props.selectedTab}Config`][props.selectedWidget.id][`${option.id}Opacity`] = context.editingStatus[option.id].opacity;
          }
        }
        if (option?.id.includes("Text") && attribute && value) {
          newWidgets[`${props.selectedTab}Config`][props.selectedWidget.id][attribute] = value;
        }
        await saveWidgetsConfig(dispatch, newWidgets);
        setIsSaving(false);
        setHasChanged(false);
        setTextPickerId(undefined);
      } catch(e) {
        console.error(e);
        setIsSaving(false);
        setHasChanged(false);
        setTextPickerId(undefined);
      }
    }
  }

  const resetChanges = () => {
    if (option.type !== "text") setTextPickerId(undefined);
    const newEditingStatus = cloneDeep(context.editingStatus);
    if (option) {
      newEditingStatus[option.id] = {
        id: props.selectedWidget.id,
        color: props.widgetsConfig[props.selectedWidget.id][`${option.id}Color`],
        opacity: props.widgetsConfig[props.selectedWidget.id][`${option.id}Opacity`],
      }
    }
    context.handleEditingStatus(newEditingStatus);
    setHasChanged(false);
  }

  const openTextColorPicker = (id: string) => {
    if (id === textPickerId) setTextPickerId(undefined);
    else setTextPickerId(id);
    setOption({...option, id: id.replace("Tag", "Text")});
  }

  const cancelColorChanges = () => {
    resetChanges();
    setTextPickerId(undefined);
  }

  React.useEffect(() => {
    if (willClose) {
      if (!isSaving) props.onClose();
      else setWillClose(false);
    }
  }, [willClose]);

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    resetChanges();
  }, [option.id]);

  const color = context.editingStatus[option?.id]?.color ?? props.widgetsConfig[props.selectedWidget.id][`${option.id}Color`] ?? "#FFFFFF";
  const opacity = context.editingStatus[option?.id] ? getFixedOpacity(context.editingStatus[option.id].opacity) : getFixedOpacity(props.widgetsConfig[props.selectedWidget.id][`${option.id}Opacity`]);

  const renderColorPicker = (absolute?: boolean) => {
    return (
      <S.ColorPicker absolute={absolute} >
        {
          absolute &&
          <S.ColorPickerTitle>
            Color Picker
          </S.ColorPickerTitle>
        }
        <S.ColorGeneratorContainer absolute={absolute} >
          <ColorGenerator
            show={true} 
            color={color} 
            showInputText={true}
            onChange={onChangeColor}
            paletteStyle={{height: absolute ? '99px' : undefined}}
          />
        </S.ColorGeneratorContainer>
        <S.ColorActionsContainer absolute={absolute} >
          <S.ColorActions>
            <TCButton
                type={TC_BUTTON_TYPE.LISMA}
                onClick={cancelColorChanges}
                disabled={(isSaving || !hasChanged) && !Boolean(textPickerId)}
            >
                Cancel
            </TCButton>
            <TCButton
                type={TC_BUTTON_TYPE.LISMA}
                onClick={onFinishChange}
                disabled={isSaving || !hasChanged}
            >
                {isSaving ? `...` : `Add`}
            </TCButton>
          </S.ColorActions>
        </S.ColorActionsContainer>
      </S.ColorPicker>
    );
  }

  const renderTextPicker = (textProps: T.ITextProps) => {
    return (
      <S.TextPicker>
        { 
          textProps.id !== "bodyTag" ?
            <ComboBox
              {...textProps}
              required={false}
              light={true}
              disabled={isSaving}
              position="bottom"
              chooseOption={onFinishChange}
              titleStyle={S.comboboxTitleStyle}
              inputContainerStyle={S.comboboxInputStyle}
              optionContainerStyle={S.optionContainerStyle}
            />
          :
            <S.TextLabel>
              Body Text
            </S.TextLabel>
        }
        <S.TextColor
          onClick={() => openTextColorPicker(textProps.id)}
          backgroundColorValue={textProps.id === textPickerId ? context.editingStatus[option?.id]?.color : props.widgetsConfig[props.selectedWidget.id][`${textProps.id.replace("Tag", "Text")}Color`]}
        />
        {
          textProps.id === textPickerId &&
          renderColorPicker(true)
        }
      </S.TextPicker>
    );
  }
  
  return (
    <S.Root
      ref={componentRef}
      style={{
        ...props.style,
        right: `30px`,
        top: `${props.posY + 95}px`,
      }}
    >
      <S.Top>
        {props.selectedWidget.label}
      </S.Top>
      <S.Bottom>
        <S.VerticalOptions>
          {
            <S.Option
              onClick={() => setOption(DEFAULT_OPTION)}
              selected={option.id === "background"}
            >
              <EditColorIcon />
            </S.Option>
          }
          {
            <S.Option
              onClick={() => setOption({
                id: props.selectedWidget.id !== "providersRating" ? "titleText" : "bodyText",
                type: "text",
                label: "Text Styles"
              })}
              selected={option.id.includes("Text")}
            >
              <EditTextIcon />
            </S.Option>
          }
          {
            props.selectedWidget.id !== "leaveReview" &&
            <S.Option
              onClick={() => setOption({
                id: "star",
                type: "color",
                label: "Star Fill"
              })}
              selected={option.id === "star"}
            >
              <EditStarIcon />
            </S.Option>
          }
          {
            props.selectedWidget.id === "providersRating" &&
            <S.Option
              onClick={() => setOption({
                id: "link",
                type: "color",
                label: "Link Color Fill"
              })}
              selected={option.id === "link"}
            >
              <LinkIcon />
            </S.Option>
          }
          {
            props.selectedWidget.id === "topReviews" &&
            <>
              <S.Option
                onClick={() => setOption({
                  id: "shadow",
                  type: "color",
                  label: "Border and Shadow Color"
                })}
                selected={option.id === "shadow"}
              >
                <EditShadowIcon />
              </S.Option>
              <S.Option
                onClick={() => setOption({
                  id: "dot",
                  type: "color",
                  label: "Carousel Dot Fill"
                })}
                selected={option.id === "dot"}
              >
                <EditDotIcon />
              </S.Option>
            </>
          }
          {props.selectedWidget.id === "reviewList" &&
            <>
            <S.Option
              onClick={() => setOption({
                id: "button",
                type: "color",
                label: "Load More Button Fill"
              })}
              selected={option.id === "button"}
            >
              <EditButtonIcon />
            </S.Option>
            <S.Option
              onClick={() => setOption({
                id: "buttonText",
                type: "color",
                label: "Load More Button Text"
              })}
              selected={option.id === "buttonText"}
            >
              <EditButtonTextIcon />
            </S.Option>
          </>}
        </S.VerticalOptions>
        { 
          option &&
          <S.Content>
            <S.Title>
              {option.label}
            </S.Title>
            {
              option.type === "color" &&
              <S.Controls>
                <S.ColorContainer>
                  <S.Color
                    backgroundColorValue={color}
                  />
                  <S.ColorTextSliderContainer>
                    <S.ColorAuxText>
                      Opacity
                    </S.ColorAuxText>
                    <S.ColorSliderContainer>
                      <Slider
                        id={`${option.id}-opacity-slider`}
                        value={opacity}
                        handleValue={onFinishChangeOpacity}
                        onChange={onChangeOpacity}
                        minValue={0}
                        maxValue={100}
                        barWidth={125}
                        barHeight={2}
                        sphereRadius={6}
                      />
                      <S.OpacityPercentage>
                        {`${opacity}%`}
                      </S.OpacityPercentage>
                    </S.ColorSliderContainer>
                  </S.ColorTextSliderContainer>
                </S.ColorContainer>
                {renderColorPicker()}
              </S.Controls>
            }
            {
              (option.type === "text" && reviewsConfig) &&
              <>
                {
                  props.selectedWidget.id !== "providersRating" &&
                  renderTextPicker({
                    id: "titleTag",
                    placeholder: "Html Tag",
                    title: "Header Text",
                    value: props.widgetsConfig[props.selectedWidget.id]?.titleTag?.toUpperCase(),
                    options: reviewsConfig.generalConfig.styles.titleTags ? reviewsConfig.generalConfig.styles.titleTags.map(t => t.toUpperCase()) : [],
                  })
                }
                {
                  props.selectedWidget.id === "overallRating" &&
                  renderTextPicker({
                    id: "subtitleTag",
                    placeholder: "Html Tag",
                    title: "Secondary Text",
                    value: props.widgetsConfig[props.selectedWidget.id]?.subtitleTag?.toUpperCase(),
                    options: reviewsConfig.generalConfig.styles.subtitleTags ? reviewsConfig.generalConfig.styles.subtitleTags.map(t => t.toUpperCase()) : [],
                  })
                }
                {
                  props.selectedWidget.id !== "leaveReview" &&
                  renderTextPicker({
                    id: "bodyTag",
                    placeholder: "Body",
                    title: "Body Text",
                    options: [],
                  })
                }
              </>
            }
          </S.Content>
        }
      </S.Bottom>
    </S.Root>
  );
}

export default StylesPanel;